import { hasAdminRole, ViewType } from '../types/dashboard';

/**
 * Lo que hace falta de un plan para pintar su entrada en el selector. Es un
 * subconjunto estructural de `UserPlanI` (`GET /users/plans`) a propósito: el
 * selector no necesita saldos ni fechas de aportación, y pedir el DTO entero
 * ataría estos helpers —y sus tests— a un contrato mucho más grande.
 */
export interface ViewOptionPlan {
	id: string;
	type: 'EMPLOYMENT' | 'INDIVIDUAL' | 'FREELANCE' | null;
	status: 'active' | 'suspended' | 'archived';
	planDisplayName: string | null;
	companyName: string | null;
	createdAt: string;
}

/** Una entrada del selector: o el panel de empresa, o UN plan concreto. */
export interface ViewOption {
	/** `'admin'`, `'fallback-user'` o el `user_epp_config.id` del plan. */
	key: string;
	viewType: ViewType;
	/** La FILA que se está operando. `null` en Admin y en el fallback. */
	planId: string | null;
	/** Clave de traducción del prefijo (`general.avatar.*`). */
	labelKey: string;
	/** Lo que distingue esta entrada de sus hermanas. `null` si no se sabe. */
	name: string | null;
	/** El plan tiene dinero pero no aporta: elegirlo sin saberlo es mirar el plan que se dejó atrás. */
	isSuspended: boolean;
	/** Plan individual en proceso: todavía no hay fila, así que tampoco `planId`. */
	isPending?: boolean;
}

const ADMIN_LABEL_KEY = 'general.avatar.admin';
const EMPLOYEE_LABEL_KEY = 'general.avatar.user';
const PERSONAL_LABEL_KEY = 'general.avatar.individual';
/** Clave de la entrada de empresa. No es un plan, así que no tiene fila. */
export const ADMIN_OPTION_KEY = 'admin';

/**
 * Clave de la entrada de empleado que se sirve mientras NO se sabe qué planes
 * tiene la persona. Ver el fail-open de `resolveViewOptions`.
 */
export const FALLBACK_USER_OPTION_KEY = 'fallback-user';

/**
 * Clave de la entrada del plan individual EN PROCESO: un empleado B2B con su alta
 * del funnel viva y sin plan personal todavía. No es una fila, así que no tiene id.
 */
export const PENDING_PERSONAL_OPTION_KEY = 'pending-individual';

/** La entrada «Resumen» del selector: la vista agregada de todos los planes (chirigota#585). */
export const RESUMEN_OPTION_KEY = 'resumen';

const pendingPersonalOption = (): ViewOption => ({
	key: PENDING_PERSONAL_OPTION_KEY,
	viewType: ViewType.INDIVIDUAL,
	planId: null,
	labelKey: PERSONAL_LABEL_KEY,
	name: null,
	isSuspended: false,
	isPending: true
});

const adminOption = (): ViewOption => ({
	key: ADMIN_OPTION_KEY,
	viewType: ViewType.ADMIN,
	planId: null,
	labelKey: ADMIN_LABEL_KEY,
	name: null,
	isSuspended: false
});

/**
 * La vista a la que lleva un plan. `type` es nullable en BBDD, así que sin él
 * decide la empresa — el mismo criterio legacy que aplica el backend en
 * `classifyUserEppConfigKind`.
 */
export const viewTypeOfPlan = (plan: ViewOptionPlan): ViewType => {
	if (plan.type === 'EMPLOYMENT') return ViewType.USER;
	if (plan.type === 'INDIVIDUAL' || plan.type === 'FREELANCE') return ViewType.INDIVIDUAL;
	return plan.companyName ? ViewType.USER : ViewType.INDIVIDUAL;
};

/**
 * ¿Este plan lo opera la vista PERSONAL?
 *
 * 🔴 Existe para que el selector y el Dashboard clasifiquen igual. Cuando cada
 * uno tenía su criterio —aquí `viewTypeOfPlan`, allí un `type === 'INDIVIDUAL'
 * || type === 'FREELANCE'`— una fila con `type` NULL salía como entrada
 * personal en el selector y desaparecía de la lista de la vista: se elegía su
 * id y se mandaba con el `mode` del flag, que es el 400 del 02-sep otra vez.
 */
export const isPersonalViewPlan = (plan: ViewOptionPlan): boolean =>
	viewTypeOfPlan(plan) === ViewType.INDIVIDUAL;

/**
 * «Vivo» = no archivado. Un plan archivado no se sincroniza ni se pinta en
 * ningún sitio; uno en suspenso sí (tiene dinero que mirar). Es el ÚNICO
 * predicado de «plan vivo»: lo comparten el selector, las vistas permitidas y
 * el Dashboard para que no puedan divergir.
 */
export const isLivePlan = (plan: ViewOptionPlan): boolean => plan.status !== 'archived';

/**
 * Lo que distingue una entrada de sus hermanas.
 *
 * 🔴 En EMPLEO es la EMPRESA, no el plan: dos empleos pueden compartir el mismo
 * producto (partícipe en suspenso) y nombrarlos por el plan daría dos entradas
 * idénticas. En los personales es al revés — no hay empresa, y lo que el
 * usuario reconoce es el nombre comercial.
 */
const nameOfPlan = (plan: ViewOptionPlan, viewType: ViewType): string | null =>
	viewType === ViewType.USER
		? plan.companyName ?? plan.planDisplayName ?? null
		: plan.planDisplayName ?? plan.companyName ?? null;

/**
 * Las entradas del selector, en el orden en que se ofrecen.
 *
 * **Una entrada = una fila de `user_epp_config`.** No hay un caso por tipología:
 * las combinaciones —empleado, autónomo, mixto, dos individuales, partícipe en
 * suspenso— salen de la misma regla, y una tipología nueva sale sola.
 *
 * Sustituye al criterio anterior, que derivaba las vistas de los ROLES y los
 * flags de la persona (el rol `individual`, retirado el 03-sep-2026, e
 * `isFreelance`). Dos motivos por los que aquello no podía funcionar, medidos
 * en PROD el 02-sep-2026:
 *   · 73 personas tienen un plan personal y NO tienen el rol `individual`: el
 *     rol no describe qué planes tienes.
 *   · la entrada de empleado se pintaba SIEMPRE, también a un B2C sin empresa,
 *     porque nadie le había puesto puerta.
 *
 * `plans === undefined` significa «todavía no se sabe» (la llamada no ha vuelto
 * o falló) y se resuelve **fail-open**: se conserva la entrada de empleado
 * genérica de siempre. Sin eso, un admin con la red lenta se quedaría sin
 * forma de volver a su vista de empleado. Una lista VACÍA sí es una respuesta:
 * quien no tiene planes no tiene entradas.
 *
 * `hasPendingPersonalPlan` añade, detrás de sus planes, la entrada del plan
 * individual en proceso. Solo si la lista ya se sabe y no trae un plan personal
 * vivo: en cuanto existe el plan real, es él quien ocupa ese sitio.
 */
export const resolveViewOptions = (
	plans: ViewOptionPlan[] | undefined,
	roles: string[] | undefined | null,
	hasPendingPersonalPlan = false
): ViewOption[] => {
	const options: ViewOption[] = hasAdminRole(roles) ? [adminOption()] : [];

	if (!plans) {
		return [
			...options,
			{
				key: FALLBACK_USER_OPTION_KEY,
				viewType: ViewType.USER,
				planId: null,
				labelKey: EMPLOYEE_LABEL_KEY,
				name: null,
				isSuspended: false
			}
		];
	}

	// Copia antes de ordenar: el array llega de un estado de React y ordenarlo
	// en sitio lo mutaría por debajo de quien lo tenga.
	const ordered = [...plans].filter(isLivePlan).sort((a, b) => {
		const byViewType =
			Number(viewTypeOfPlan(a) === ViewType.INDIVIDUAL) -
			Number(viewTypeOfPlan(b) === ViewType.INDIVIDUAL);
		if (byViewType !== 0) return byViewType;
		// 🔴 El ESTADO manda sobre la antigüedad, igual que el backend cuando
		// tiene que elegir un plan sin que se lo digan (`PLAN_DETERMINISTIC_ORDER`:
		// `status ASC, created_at ASC`, con `'active' < 'suspended'`). Ordenar sólo
		// por fecha preseleccionaría el plan PARADO a quien tiene uno viejo en
		// suspenso y otro vivo: la etiqueta diría un plan y la vista enseñaría otro.
		const byStatus = Number(a.status !== 'active') - Number(b.status !== 'active');
		if (byStatus !== 0) return byStatus;
		const byAge = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
		// El `id` hace el orden TOTAL: dos filas creadas en la misma transacción
		// comparten `created_at`, y sin desempate las entradas bailarían entre
		// renders según cómo llegara el array.
		return byAge !== 0 ? byAge : a.id.localeCompare(b.id);
	});

	const pending =
		hasPendingPersonalPlan && !ordered.some(isPersonalViewPlan) ? [pendingPersonalOption()] : [];

	return [
		...options,
		...ordered.map((plan): ViewOption => {
			const viewType = viewTypeOfPlan(plan);
			return {
				key: plan.id,
				viewType,
				planId: plan.id,
				labelKey: viewType === ViewType.USER ? EMPLOYEE_LABEL_KEY : PERSONAL_LABEL_KEY,
				name: nameOfPlan(plan, viewType),
				isSuspended: plan.status !== 'active'
			};
		}),
		...pending
	];
};

/**
 * Vistas a las que se tiene derecho, en el orden en que se ofrecen. Corrige el
 * `viewType` de arranque: el que sale de `AccountType` no sabe qué roles hay
 * (cualquier segundo rol es `BOTH`) y tampoco qué planes.
 *
 * Se deriva de `resolveViewOptions` a propósito: es la misma pregunta que el
 * selector, y dos derivaciones paralelas acabarían discrepando (suspensos, una
 * tipología nueva…). La única diferencia es que `USER` siempre está
 * (fail-open): es la vista base, y la que conserva a quien tiene la lista de
 * planes sin cargar. La PERSONAL se gana con un plan personal vivo — no con un
 * rol. El rol `individual` que hacía de puerta se retiró el 03-sep-2026: se
 * ponía por SQL a mano, y 73 personas con plan personal no lo tenían.
 *
 * El plan individual EN PROCESO también da derecho a la vista personal: es donde
 * se compara, se confirma el plan y se arranca el KYC.
 */
export const resolveAvailableViews = (
	roles: string[] | undefined | null,
	plans: ViewOptionPlan[] | undefined,
	hasPendingPersonalPlan = false
): ViewType[] =>
	Array.from(
		new Set([
			...(hasAdminRole(roles) ? [ViewType.ADMIN] : []),
			ViewType.USER,
			...resolveViewOptions(plans, roles, hasPendingPersonalPlan).map((option) => option.viewType)
		])
	);

/**
 * Cuál de las entradas se está viendo ahora mismo.
 *
 * No es estado nuevo: se deriva de los dos que ya existen (`viewType` y el plan
 * elegido) más la lista. Un tercer estado habría que mantenerlo sincronizado
 * con los otros dos, que es de donde salen los bugs de «la pantalla dice un
 * plan y enseña otro».
 *
 * El último escalón —servir la primera opción cuando la vista no casa con
 * ninguna— no es un apaño: cubre a las 73 personas con plan personal que
 * arrancan en `viewType = USER` porque al arrancar aún no se saben los planes
 * (`resolveAvailableViews` sin lista). Antes eso lo tapaba un `|| isIndividual`
 * en la etiqueta, que hacía que la etiqueta contradijera a la vista.
 */
export const resolveSelectedViewOption = (
	options: ViewOption[],
	viewType: string,
	selectedPlanId: string | null | undefined
): ViewOption | null => {
	const named = options.find(
		(option) => option.planId === selectedPlanId && option.viewType === viewType
	);
	if (named) return named;

	const byView = options.find((option) => option.viewType === viewType);
	if (byView) return byView;

	return options[0] ?? null;
};

/**
 * La etiqueta que se pinta. Vive aquí y no en cada componente para que el
 * sidebar de escritorio y el nav móvil no puedan divergir: hoy tienen la misma
 * lógica copiada, y por eso la entrada sin puerta existía dos veces.
 */
export const formatViewOptionLabel = (
	option: ViewOption | null | undefined,
	translate: (key: string) => string
): string => {
	if (!option) return '';
	const base = translate(option.labelKey);
	// 🔴 El plan parado NO se etiqueta: «(en suspenso)» es jerga nuestra y a quien
	// lo lee le suena a que su plan tiene un problema, cuando sólo significa que
	// ya no entran aportaciones. Decisión de producto, 07-sep-2026.
	return option.name ? `${base} · ${option.name}` : base;
};
